import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { NoteIcon } from 'app/agriculture/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { TextArea } from 'app/agriculture/forms/components/TextArea/TextArea';
import { EntityParentSelectField } from 'app/agriculture/forms/components/EntityParentSelectField/EntityParentSelectField';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { SpecificLocationField } from 'app/agriculture/forms/components/SpecificLocationField/SpecificLocationField';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { useInitialParent } from 'app/agriculture/router/useInitialParent';
import './NoteCreateForm.scss';
import { useEntityCreateMutation } from 'app/agriculture/mutations/entity/create/useEntityCreateMutation';
import { useAttachOptions } from 'app/agriculture/hooks/useAttachOptions/useAttachOptions';
import { noteAttachOptionTypes } from 'app/agriculture/routes/Notes/utils/noteAttachOptionTypes';

interface NoteCreateFields {
    text: string;
    parentId?: string | number | null;
    location?: MapLocation | null;
}

export function NoteCreateForm() {
    const navigate = useNavigate();
    const routeActions = useRouteActions();
    const initialParent = useInitialParent();
    const formController = useForm<NoteCreateFields>({
        mode: 'all',
        defaultValues: {
            parentId: initialParent?.id,
        },
        shouldFocusError: true,
    });
    const {
        register,
        setValue,
        handleSubmit,
        formState: { errors },
        reset,
    } = formController;

    const { mutate, isPending } = useEntityCreateMutation('Note');
    const { agricultureToastService } = useContext(ServicesContext);

    function onSuccess({ id }: { id: string }) {
        agricultureToastService
            .showToast({
                title: 'Note has been saved',
                subtext: 'The note was saved successfully!',
                type: AgricultureToastType.SUCCESS,
                variant: 'detailed',
                actions: [
                    {
                        text: 'Add Another Note',
                        onClick: () => {
                            routeActions.create('Note');
                        },
                        startIcon: 'add',
                    },
                    {
                        text: 'Edit Note',
                        onClick: () => {
                            routeActions.edit(id, { type: 'Note' });
                        },
                        startIcon: 'edit',
                        variant: 'textonly',
                    },
                ],
            })
            .catch((e: unknown) => {
                console.error(e);
            });
        reset();
        routeActions.goto(id, { type: 'Note' });
    }

    const onSubmit: SubmitHandler<NoteCreateFields> = (formData) => {
        mutate(formData, { onSuccess });
    };

    const attachOptions = useAttachOptions(noteAttachOptionTypes);

    return (
        <ContentBox title="Create Note" Icon={NoteIcon} className="NoteCreateForm" variant="md">
            <FormProvider {...formController}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section scrollable className="NoteCreateForm__fields">
                        <TextArea
                            label="Note"
                            {...register('text', { required: 'Please add a note' })}
                            className="NoteCreate__text-input"
                            errorMessage={errors.text?.message}
                        />
                        <EntityParentSelectField
                            label="Attach Note To"
                            attachOptions={attachOptions}
                        />
                        <SpecificLocationField
                            isPin
                            entityType="Note"
                            initialValue={null}
                            onChange={(value) => {
                                setValue('location', value, { shouldDirty: true });
                            }}
                        />
                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Create note',
                                type: 'submit',
                                isLoading: isPending,
                            }}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
